import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, ExternalLink } from 'lucide-react';

interface Profile {
  user_id: string;
  full_name: string | null;
  email: string | null;
  school_id: string | null;
  created_at: string;
}

interface UserRole {
  role: string;
}

interface NotificationPrefs {
  monthly_digest: boolean;
  billing_reminders: boolean;
  product_updates: boolean;
  marketing_consent_at: string | null;
}

interface School {
  id: string;
  name: string;
  plan_tier: string;
  plan_sub_tier: string | null;
  billing_status: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  last_invoice_paid_at: string | null;
}

const SCHOOL_ROLES = ['teacher', 'slt', 'department_leader', 'school_admin'] as const;

function billingStatusClass(status: string) {
  if (status === 'active' || status === 'trialing') return 'bg-green-100 text-green-800 border-green-200';
  if (status === 'past_due') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-red-100 text-red-800 border-red-200';
}

function stripeCustomerUrl(id: string) {
  return `https://dashboard.stripe.com/customers/${id}`;
}
function stripeSubscriptionUrl(id: string) {
  return `https://dashboard.stripe.com/subscriptions/${id}`;
}

export default function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [newRole, setNewRole] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [prefsUpdating, setPrefsUpdating] = useState(false);

  const { data: profile, isLoading: profileLoading, error: profileError, refetch } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, full_name, email, school_id, created_at')
        .eq('user_id', userId!)
        .single();
      if (error) throw error;
      return data as Profile;
    },
    enabled: !!userId,
  });

  const { data: roles = [], refetch: refetchRoles } = useQuery({
    queryKey: ['admin-user-roles', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId!);
      if (error) throw error;
      return data as UserRole[];
    },
    enabled: !!userId,
  });

  const { data: school } = useQuery({
    queryKey: ['admin-user-school', profile?.school_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('id, name, plan_tier, plan_sub_tier, billing_status, stripe_customer_id, stripe_subscription_id, current_period_start, current_period_end, last_invoice_paid_at')
        .eq('id', profile!.school_id!)
        .single();
      if (error) throw error;
      return data as School;
    },
    enabled: !!profile?.school_id,
  });

  const { data: notificationPrefs, refetch: refetchPrefs } = useQuery({
    queryKey: ['admin-user-notification-prefs', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('monthly_digest, billing_reminders, product_updates, marketing_consent_at')
        .eq('user_id', userId!)
        .maybeSingle();
      if (error) throw error;
      return data as NotificationPrefs | null;
    },
    enabled: !!userId,
  });

  const { data: assessmentCount } = useQuery({
    queryKey: ['admin-user-assessments', userId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('writing_samples')
        .select('id', { count: 'exact', head: true })
        .eq('uploaded_by', userId!);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!userId,
  });

  async function callAdminUserMgmt(body: Record<string, unknown>) {
    const resp = await supabase.functions.invoke('admin-user-mgmt', { body });
    if (resp.error) throw new Error(resp.error.message);
    const json = resp.data as { ok?: boolean; error?: string };
    if (json?.error) throw new Error(json.error);
    return json;
  }

  const handlePasswordReset = async () => {
    setActionLoading(true);
    try {
      await callAdminUserMgmt({ action: 'send_password_reset', targetUserId: userId });
      toast({ title: 'Password reset sent', description: `Reset email sent to ${profile?.email}.` });
      setResetDialogOpen(false);
    } catch (err) {
      toast({ title: 'Failed', description: (err as Error).message, variant: 'destructive' });
    }
    setActionLoading(false);
  };

  const handleUpdateEmail = async () => {
    setActionLoading(true);
    try {
      await callAdminUserMgmt({ action: 'update_email', targetUserId: userId, newEmail });
      toast({ title: 'Email updated', description: `Email changed to ${newEmail}.` });
      setEmailDialogOpen(false);
      setNewEmail('');
      refetch();
    } catch (err) {
      toast({ title: 'Failed', description: (err as Error).message, variant: 'destructive' });
    }
    setActionLoading(false);
  };

  const handleRemoveFromSchool = async () => {
    setActionLoading(true);
    try {
      await callAdminUserMgmt({ action: 'remove_from_school', targetUserId: userId, reason: removeReason });
      toast({ title: 'User removed from school' });
      setRemoveDialogOpen(false);
      setRemoveReason('');
      navigate('/admin/users');
    } catch (err) {
      toast({ title: 'Failed', description: (err as Error).message, variant: 'destructive' });
    }
    setActionLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (!deleteReason.trim()) return;
    setActionLoading(true);
    try {
      await callAdminUserMgmt({ action: 'delete_account', targetUserId: userId, reason: deleteReason });
      toast({ title: 'Account deleted', description: `${profile?.email ?? 'User'}'s account and all associated data have been removed.` });
      setDeleteDialogOpen(false);
      navigate('/admin/users');
    } catch (err) {
      toast({ title: 'Failed', description: (err as Error).message, variant: 'destructive' });
    }
    setActionLoading(false);
  };

  const handleSetRole = async () => {
    if (!newRole) return;
    setActionLoading(true);
    try {
      await callAdminUserMgmt({ action: 'set_role', targetUserId: userId, newRole });
      toast({ title: 'Role updated', description: `Role changed to ${newRole.replace('_', ' ')}.` });
      setRoleDialogOpen(false);
      setNewRole('');
      refetchRoles();
    } catch (err) {
      toast({ title: 'Failed', description: (err as Error).message, variant: 'destructive' });
    }
    setActionLoading(false);
  };

  const handleUpdateNotificationPref = async (
    field: keyof Omit<NotificationPrefs, 'marketing_consent_at'>,
    value: boolean,
  ) => {
    if (!notificationPrefs) return;
    setPrefsUpdating(true);
    const updated = { ...notificationPrefs, [field]: value };
    try {
      await callAdminUserMgmt({
        action: 'update_notification_prefs',
        targetUserId: userId,
        monthly_digest: updated.monthly_digest,
        billing_reminders: updated.billing_reminders,
        product_updates: updated.product_updates,
      });
      refetchPrefs();
    } catch (err) {
      toast({ title: 'Failed to update preference', description: (err as Error).message, variant: 'destructive' });
    }
    setPrefsUpdating(false);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Derive the current school-scoped role (excludes super_admin)
  const schoolRole = roles.find(r => r.role !== 'super_admin')?.role ?? null;
  const isIndividualPlan = school?.plan_tier === 'individual';

  if (profileLoading) {
    return <DashboardLayout><div className="p-6 text-sm text-muted-foreground">Loading…</div></DashboardLayout>;
  }

  if (profileError || !profile) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-2">
          <p className="text-sm font-medium text-destructive">Failed to load user.</p>
          {profileError && <p className="text-xs text-muted-foreground">{(profileError as Error).message}</p>}
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/users')}>Back to users</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/users')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{profile.full_name ?? 'Unknown user'}</h1>
            <p className="text-muted-foreground text-sm">{profile.email ?? '—'}</p>
          </div>
        </div>

        {/* Profile */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Profile</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <span className="text-muted-foreground">Joined</span>
              <p className="font-medium">{formatDate(profile.created_at)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Roles</span>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {roles.length === 0
                  ? <span className="font-medium">—</span>
                  : roles.map(r => (
                    <Badge key={r.role} variant="secondary" className="text-xs">{r.role.replace(/_/g, ' ')}</Badge>
                  ))}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">School</span>
              {school ? (
                <p className="font-medium text-primary cursor-pointer hover:underline"
                  onClick={() => navigate(`/admin/schools/${school.id}`)}>
                  {school.name}
                </p>
              ) : <p className="font-medium">—</p>}
            </div>
            <div>
              <span className="text-muted-foreground">Total submissions</span>
              <p className="font-medium">{assessmentCount ?? '—'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Communication Preferences */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Communication Preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0 text-sm">
            {(['monthly_digest', 'billing_reminders', 'product_updates'] as const).map((field) => {
              const labels: Record<typeof field, { label: string; description: string }> = {
                monthly_digest: { label: 'Monthly digest', description: 'Monthly summary of assessment activity' },
                billing_reminders: { label: 'Billing reminders', description: 'Payment, plan, and subscription alerts' },
                product_updates: { label: 'Product updates', description: 'New features, improvements, and announcements' },
              };
              const { label, description } = labels[field];
              const checked = notificationPrefs?.[field] ?? true;
              return (
                <div key={field} className="flex items-start gap-3 py-3 border-b last:border-0">
                  <Checkbox
                    id={`pref-${field}`}
                    checked={checked}
                    disabled={prefsUpdating || !notificationPrefs}
                    onCheckedChange={(v) => handleUpdateNotificationPref(field, !!v)}
                    className="mt-0.5"
                  />
                  <label htmlFor={`pref-${field}`} className="cursor-pointer select-none">
                    <p className="font-medium leading-tight">{label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                  </label>
                </div>
              );
            })}
            <div className="pt-3">
              <span className="text-muted-foreground">Marketing consent</span>
              <p className="font-medium mt-0.5">
                {notificationPrefs?.marketing_consent_at
                  ? `Consented ${formatDate(notificationPrefs.marketing_consent_at)}`
                  : '—'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Billing */}
        {school && (
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Billing</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground h-7"
                onClick={() => navigate(`/admin/schools/${school.id}`)}>
                Override billing →
              </Button>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">Plan</span>
                <div className="flex gap-1 mt-0.5">
                  <Badge variant="outline" className="text-xs">{school.plan_tier}</Badge>
                  {school.plan_sub_tier && (
                    <Badge variant="outline" className="text-xs">{school.plan_sub_tier}</Badge>
                  )}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Billing status</span>
                <div className="mt-0.5">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${billingStatusClass(school.billing_status)}`}>
                    {school.billing_status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Last payment</span>
                <p className="font-medium">
                  {formatDate(school.last_invoice_paid_at ?? school.current_period_start)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Next payment</span>
                <p className="font-medium">{formatDate(school.current_period_end)}</p>
              </div>
              {school.stripe_customer_id && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Stripe customer</span>
                  <a href={stripeCustomerUrl(school.stripe_customer_id)}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 font-mono text-xs text-primary hover:underline mt-0.5">
                    {school.stripe_customer_id}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {school.stripe_subscription_id && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Stripe subscription</span>
                  <a href={stripeSubscriptionUrl(school.stripe_subscription_id)}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 font-mono text-xs text-primary hover:underline mt-0.5">
                    {school.stripe_subscription_id}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <div>
                <p className="text-sm font-medium">Send password reset</p>
                <p className="text-xs text-muted-foreground">Sends a reset email to {profile.email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setResetDialogOpen(true)}>Send reset</Button>
            </div>

            <div className="flex items-center justify-between py-2 border-b">
              <div>
                <p className="text-sm font-medium">Update email address</p>
                <p className="text-xs text-muted-foreground">Updates both auth and profile email</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(true)}>Change email</Button>
            </div>

            {school && !isIndividualPlan && (
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <p className="text-sm font-medium">Change role</p>
                  <p className="text-xs text-muted-foreground">
                    Current: <span className="font-medium">{schoolRole?.replace(/_/g, ' ') ?? '—'}</span>
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => { setNewRole(schoolRole ?? ''); setRoleDialogOpen(true); }}>
                  Change role
                </Button>
              </div>
            )}

            {profile.school_id && (
              <div className="flex items-center justify-between py-2 border-b">
                <div>
                  <p className="text-sm font-medium text-destructive">Remove from school</p>
                  <p className="text-xs text-muted-foreground">Clears school, roles, and class assignments</p>
                </div>
                <Button variant="destructive" size="sm" onClick={() => setRemoveDialogOpen(true)}>Remove</Button>
              </div>
            )}

            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-destructive">Delete account</p>
                <p className="text-xs text-muted-foreground">Permanently removes account and all personal data (GDPR erasure)</p>
              </div>
              <Button variant="destructive" size="sm" onClick={() => { setDeleteReason(''); setDeleteDialogOpen(true); }}>Delete</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Password reset dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send password reset?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            A reset email will be sent to <strong>{profile.email}</strong>. This is logged in the audit trail.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePasswordReset} disabled={actionLoading}>
              {actionLoading ? 'Sending…' : 'Send reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update email dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update email address</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Current: <strong>{profile.email}</strong></p>
            <div className="space-y-1.5">
              <Label>New email address</Label>
              <Input type="email" placeholder="new@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateEmail} disabled={actionLoading || !newEmail.trim()}>
              {actionLoading ? 'Updating…' : 'Update email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change role dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change role</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Current role: <strong>{schoolRole?.replace(/_/g, ' ') ?? '—'}</strong>
            </p>
            <div className="space-y-1.5">
              <Label>New role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {SCHOOL_ROLES.map(r => (
                    <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSetRole} disabled={actionLoading || !newRole || newRole === schoolRole}>
              {actionLoading ? 'Saving…' : 'Save role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove from school dialog */}
      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remove from {school?.name ?? 'school'}?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              This clears the school association, all roles, and all class assignments. Assessment data is retained. The user's session will be terminated immediately.
            </p>
            <div className="space-y-1.5">
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Textarea
                placeholder="Required — describe why this user is being removed"
                value={removeReason}
                onChange={e => setRemoveReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRemoveFromSchool}
              disabled={actionLoading || !removeReason.trim()}>
              {actionLoading ? 'Removing…' : 'Remove from school'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete account dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete account permanently?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm text-muted-foreground">
            <p>This will permanently delete <strong>{profile.email ?? 'this user'}</strong>'s account, all writing submissions, assessments, and personal data. This cannot be undone.</p>
            <div className="space-y-1.5">
              <Label>Reason <span className="text-destructive">*</span></Label>
              <Textarea
                placeholder="Required — reason for GDPR erasure or account removal"
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteAccount}
              disabled={actionLoading || !deleteReason.trim()}>
              {actionLoading ? 'Deleting…' : 'Delete account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
